import { ErrorBoundary } from "@/components/error-boundary";
import { useIsTouchDevice } from "@/hooks/use-mobile";
import { mapDefaults, mapInteractionTimeouts } from "@/info/map-defaults";
import { useSearchPageInteractionStore } from "@/providers/search";
import { useOptimizedClicked } from "@/providers/search/hooks/use-optimized-clicked";
import { usePropertyInteractionStore } from "@/stores/property-interaction";
import type { MapItemWithCoodId } from "@/types/search";
import { logger } from "@/utils/logger";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import React from "react";

import { cn } from "@/utils/cn";
import { SafePin } from "./safe-pin";

const MarkerFallback = ({ children }: { children?: React.ReactNode }) => (
	<div className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs relative">
		<div className="absolute top-full left-0 z-[-1]">{children}</div>
	</div>
);

// Memoized individual marker component to prevent unnecessary re-renders
export const ProjectMarker = React.memo(
	function MarkerComponent({
		marker,
		index,
		className,
		currentZoom,
	}: {
		marker: MapItemWithCoodId;
		index: number;
		className?: string;
		currentZoom: number;
	}) {
		// Parse coordinates once
		const coordinates = React.useMemo(() => {
			if (!marker || !marker._id) return null;

			let coords: [number, number];
			if (typeof marker._id === "string") {
				try {
					coords = JSON.parse(`[${marker._id}]`);
				} catch (err) {
					logger.error("Failed to parse marker coordinates:", err);
					return null;
				}
			} else if (Array.isArray(marker._id)) {
				coords = marker._id as [number, number];
			} else {
				logger.error("Invalid marker _id format:", marker._id);
				return null;
			}

			if (!Array.isArray(coords) || coords.length !== 2 || isNaN(coords[0]) || isNaN(coords[1])) {
				logger.error("Invalid coordinates:" + JSON.stringify(coords), coords);
				return null;
			}

			return coords;
		}, [marker]);

		const coordinatesString = React.useMemo(() => coordinates?.toString() || "", [coordinates]);

		// Zero-rerender hover system using refs
		const markerRef = React.useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
		const safePinRef = React.useRef<HTMLDivElement | null>(null);
		const projectNameRef = React.useRef<HTMLDivElement | null>(null);
		const isHoveredRef = React.useRef(false);
		const isGloballyHoveredRef = React.useRef(false);
		const isMobileTappedRef = React.useRef(false);
		const tapTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

		// Use optimized click state only (doesn't cause re-renders during hover)
		const { isClicked, handleToggleClick } = useOptimizedClicked(coordinatesString);

		// Get store instance for direct subscription
		const store = useSearchPageInteractionStore();

		// Get property overlay hook from PropertyInteractionProvider
		const openPropertyOverlay = usePropertyInteractionStore(state => state.openPropertyOverlay);

		const isUnit = (marker.sups?.length || 0) > 0;
		const supsCount = marker.sups?.length || 0;
		const baseZIndex = supsCount + 1; // Ensure minimum z-index of 1, maintains relative ordering
		// Show name on hover/tap only when zoom is below threshold
		const showNameOnHoverOnly = currentZoom < mapDefaults.projectNameZoomThreshold;
		// Detect if device is touch-based (more accurate than checking for touchstart)
		const isTouchDevice = useIsTouchDevice();

		// Combined marker effects: secondary with scale on hover or clicked, else view icon with bg-accent no scale if viewed
		const applyMarkerEffects = React.useCallback(
			(isHovered: boolean, isTapped = false) => {
				if (!safePinRef.current) return;

				const element = safePinRef.current;

				// Clear all classes first
				element.classList.remove(
					"scale-150",
					"!bg-secondary",
					"text-black",
					"shadow-lg",
					"!bg-accent",
					"text-accent-foreground",
				);

				// Apply effects based on priority: hover/clicked > viewed > default
				if (isHovered || isClicked) {
					// Secondary with scale on hover or clicked
					element.classList.add("scale-150", "!bg-secondary", "text-black", "shadow-lg");
				}
				if (isUnit && !isHovered && !isClicked) {
					// View icon with bg-accent no scale if viewed
					element.classList.add("!bg-accent", "text-accent-foreground");
				}

				// Update AdvancedMarker zIndex
				if (markerRef.current) {
					const zIndex = isHovered || isGloballyHoveredRef.current || isClicked ? 9999 : baseZIndex;
					markerRef.current.zIndex = zIndex;
				}

				// Handle project name visibility based on zoom level
				if (showNameOnHoverOnly && projectNameRef.current) {
					// On touch devices: show on tap, on desktop: show on hover
					const shouldShow = isTouchDevice ? isTapped : isHovered;
					if (shouldShow) {
						projectNameRef.current.style.opacity = "1";
						projectNameRef.current.style.pointerEvents = "auto";
					} else {
						projectNameRef.current.style.opacity = "0";
						projectNameRef.current.style.pointerEvents = "none";
					}
				}
			},
			[isClicked, isUnit, showNameOnHoverOnly, isTouchDevice, baseZIndex],
		);

		// Apply effects when states change
		React.useEffect(() => {
			applyMarkerEffects(isHoveredRef.current || isGloballyHoveredRef.current, isMobileTappedRef.current);
		}, [applyMarkerEffects]);

		// Cleanup tap timeout on unmount
		React.useEffect(() => {
			return () => {
				if (tapTimeoutRef.current) {
					clearTimeout(tapTimeoutRef.current);
				}
			};
		}, []);

		// Zero-rerender hover handlers
		const handleMouseEnter = React.useCallback(() => {
			if (isTouchDevice) return; // Skip hover on touch devices
			isHoveredRef.current = true;
			applyMarkerEffects(true, false);
			// Update global state for cross-component sync
			store.getState().setHoveredMarkerId(coordinatesString);
		}, [applyMarkerEffects, coordinatesString, store, isTouchDevice]);

		const handleMouseLeave = React.useCallback(() => {
			if (isTouchDevice) return; // Skip hover on touch devices
			isHoveredRef.current = false;
			applyMarkerEffects(false, false);
			// Clear global state
			store.getState().setHoveredMarkerId(null);
		}, [applyMarkerEffects, store, isTouchDevice]);

		// Touch device tap handler for tooltip
		const handleTap = React.useCallback(() => {
			if (!isTouchDevice || !showNameOnHoverOnly) return;

			// Toggle tap state
			isMobileTappedRef.current = !isMobileTappedRef.current;
			applyMarkerEffects(false, isMobileTappedRef.current);

			// Auto-hide tooltip after a delay on touch devices
			if (isMobileTappedRef.current) {
				if (tapTimeoutRef.current) {
					clearTimeout(tapTimeoutRef.current);
				}
				tapTimeoutRef.current = setTimeout(() => {
					isMobileTappedRef.current = false;
					applyMarkerEffects(false, false);
				}, mapInteractionTimeouts.MOBILE_TOOLTIP_AUTO_HIDE_MS);
			}
		}, [applyMarkerEffects, isTouchDevice, showNameOnHoverOnly]);

		// Listen for global hover changes without causing re-renders
		React.useEffect(() => {
			const unsubscribe = store.subscribe(
				state => state.hoveredMarkerId,
				hoveredId => {
					const wasGloballyHovered = isGloballyHoveredRef.current;
					isGloballyHoveredRef.current = hoveredId === coordinatesString;

					// Only update if global hover state changed
					if (wasGloballyHovered !== isGloballyHoveredRef.current) {
						applyMarkerEffects(isHoveredRef.current || isGloballyHoveredRef.current);
					}
				},
			);

			return unsubscribe;
		}, [coordinatesString, applyMarkerEffects, store]);

		const onClick = React.useCallback(() => {
			if (!marker || !coordinates) return;

			// Handle mobile tap for tooltip
			handleTap();

			// Open property overlay with properties using new system
			if (marker.sups && marker.sups?.length > 0) {
				const supIds = marker.sups.map(sup => sup._id);
				const initialSupId = supIds[0];

				// Use the new PropertyOverlay system via PropertyInteractionProvider
				openPropertyOverlay({ sups: marker.sups, initialSupId, projectName: marker.projectName || "" });
			} else if (marker.projectId) {
				openPropertyOverlay({ sups: [], projectId: marker.projectId });
			}

			// Toggle selection using optimized hook with proper marker data
			handleToggleClick({
				coordinates,
				projectId: marker.projectId,
				projectName: marker.projectName,
				sups: marker.sups,
			});

			// Don't pan to marker on click to avoid center shift
		}, [marker, coordinates, handleToggleClick, openPropertyOverlay, handleTap]);

		if (!coordinates) return null;
		const markerClass = marker.isExactMatch ? "bg-primary text-white" : "bg-white text-gray-900";

		return (
			<ErrorBoundary fallback={MarkerFallback}>
				<AdvancedMarker
					key={`marker-${coordinatesString}-${index}`}
					position={{ lat: coordinates[1], lng: coordinates[0] }}
					onClick={onClick}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					zIndex={isClicked ? 9999 : baseZIndex}
					ref={markerRef}
					className={className}
				>
					<div className="relative">
						{marker.projectName && (
							<div
								ref={projectNameRef}
								className={`absolute ${markerClass} -top-8 left-1/2 transform -translate-x-1/2 text-xs px-4 py-1 rounded-full whitespace-nowrap z-10 border border-gray-300 shadow-lg flex flex-col items-center justify-center transition-opacity duration-200`}
								style={{
									opacity: showNameOnHoverOnly ? 0 : 1,
									pointerEvents: showNameOnHoverOnly ? "none" : "auto",
								}}
							>
								<span className="">{marker.projectName}</span>
								<div
									className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[4px] border-transparent border-t-${marker.isExactMatch ? "primary" : "white"}`}
								></div>
							</div>
						)}
						<SafePin
							ref={safePinRef}
							className={cn(
								"marker", // Add marker class for zoom-based sizing
								marker.sups?.length > 0 ? "!bg-secondary-700" : "!bg-secondary-200",
								"!border-secondary-700",
							)}
						>
							<div
								className={cn(
									"flex items-center justify-center",
									marker.sups?.length > 0 ? "text-white" : "text-gray-400",
								)}
							>
								{marker.sups?.length ?? "0"}
							</div>
						</SafePin>
					</div>
				</AdvancedMarker>
			</ErrorBoundary>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison to prevent unnecessary re-renders
		const m1 = prevProps.marker;
		const m2 = nextProps.marker;
		return (
			m1._id === m2._id &&
			m1.sups?.length === m2.sups?.length &&
			prevProps.index === nextProps.index &&
			m1.projectId === m2.projectId &&
			prevProps.currentZoom === nextProps.currentZoom
		);
	},
);
